import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";

import "./membershipDisplay.scss";
import { capitalize, valueToString } from "helpers/Helpers";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { faEllipsisVertical } from "@fortawesome/pro-regular-svg-icons";
import { Badge } from "components/badge/Badge";
import classNames from "classnames";

type MembershipDisplayProps = {
  id: number | string;
  title: string;
  subtitle?: string;
  type?: string;
  /**
   * null = no expiry.
   * undefined = empty card.
   * */
  expiry?: any;
  suspension_start_date?: Date;
  suspension_end_date?: any;
  indefinite_suspension?: boolean;
  suspension_reason?: string;
  logo_image_source?: string;
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  /** true = show ONLY title/subtitle in center of the card */
  emptyCard?: boolean;
  status?: string;
  status_readable?: string;
  suspendMembership?: () => void;
  unsuspendMembership?: (id: any) => void;
  disableMembership? : () => void;
  enableMembership? : () => void;
  deleteMembership?: (id: any) => void;
  moveMembership?: (id: any) => void;
  printMembership?: (id: any) => void;
  emailMembership?: () => void;
  editExpiryDate?: (id: any) => void;
};

const MembershipDisplay = (props: MembershipDisplayProps) => {
  const { t } = useTranslation();

  return (
    <div
      id={typeof props.id === "string" ? props.id : props.id.toString()}
      className={`membership-display${
        props.status === "disabled" || props.status === 'expired' && !props.emptyCard ? " membership-display-inactive" : "" 
      }`}
    >
      {props.emptyCard ? (
        <div className="empty-container">
          <p>{props.title}</p>
          <p>{props?.subtitle}</p>
        </div>
      ) : (
        <>
          <div
            style={{
              cursor: props.status === "active" && props.onClick ? "pointer" : "",
            }}
            onClick={props.status === "active" || props.status === "suspended" ? props.onClick : undefined}
            className="membership-main-content"
          >
            {props?.logo_image_source ? (
              <div className="img-container" style={{ background: "#FFFFFF" }}>
                <img className="customer-ticket-logo" src={props?.logo_image_source} alt="" />
              </div>
            ) : null}

            <div className="flex flex-col">
              {props.title ? (
                <div
                  className={classNames("membership-name", {
                    "membership-name-inactive": props.status !== "active",
                  })}
                >
                  {valueToString(props?.title)}
                </div>
              ) : (
                <div>{t("N/A")}</div>
              )}
              {props.subtitle && (
                <div className="membership-subtitle">
                  <Badge size="small" type="gray" className={props.status !== "active" && "membership-expired-badge"}>
                    <p
                      className={classNames("membership-badge-text", {
                        "membership-badge-text-inactive": props.status !== "active",
                      })}
                    >
                      {capitalize(props?.subtitle)}
                    </p>
                  </Badge>
                </div>
              )}
              {props.suspension_reason ? (
                <>
                  <p className="membership-suspension-reason">{props.suspension_reason}</p>
                </>
              ) : null}
              {props.status === "suspended" ? (
                <div>
                  <Badge size="small" type="error">
                    {props.indefinite_suspension
                      ? "Indefinite Suspension"
                      : `Suspended until ${props.suspension_end_date}`}
                  </Badge>
                </div>
              ) : null}
              {props.status === "active" && props.suspension_start_date ? (
                <div>
                  <Badge size="small" type="warning">
                    {`${t("Suspension starting on")} ${moment(props.suspension_start_date).format("LL")}`}
                  </Badge>
                </div>
              ) : null}
            </div>
          </div>

          <div className="membership-secondary-content">
            {props.status === "active" ? (
              <div>
                {props.expiry && (
                  <div className="flex flex-column gap-2">
                    <p className="membership-secondary-content_expiry_date_label">{t("EXPIRY DATE")}</p>{" "}
                    <p className="membership-secondary-content_expiry_date">
                      {moment(String(props?.expiry)).format("MMMM Do, YYYY")}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full">
                <p className="membership-expired-secondary-content_text">{capitalize(props.status)}</p>{" "}
                {props?.status === 'expired' ? (
                  <div>
                    {t('on')} {moment(String(props?.expiry)).format("MMMM Do, YYYY")}
                  </div>
                ) : null}
              </div>
            )}

            {(props.moveMembership || props.suspendMembership || props.deleteMembership) && (
              <div
                className={classNames("membership-secondary-content_dropdown_container", {
                  "membership-secondary-content_dropdown_container_inactive": props.status === "disabled" || props.status === 'expired',
                })}
              >
                <NavigationDropdownNew
                  showPlainTextLabel
                  rightAlign
                  label={
                    <FontAwesomeIcon
                      icon={faEllipsisVertical}
                    />
                  }
                  sections={[
                    [
                      {
                        label: t("Print member card"),
                        icon: "print",
                        hidden: props.status === "suspended" || props.status === 'disabled',
                        onClick: () => props.printMembership(props.id),
                      },
                      {
                        label: t("Email member card"),
                        icon: "envelope-open",
                        hidden: props.status === "suspended" || props.status === 'disabled',
                        onClick: () => props.emailMembership(),
                      },
                      {
                        label: t("Edit expiry date"),
                        icon: "edit",
                        hidden: props.status === "suspended" || props.status === 'disabled',
                        onClick: () => props.editExpiryDate(props.id),
                      },
                    ],
                    [
                      {
                        label: t("Move"),
                        icon: "turn-down-right",
                        hidden: props.status === "suspended",
                        onClick: () => props.moveMembership(props.id),
                      },
                      {
                        label: t("Edit Suspension"),
                        icon: "pencil",
                        hidden: props.status !== "suspended",
                        onClick: () => props.suspendMembership(),
                      },
                      {
                        label: "Disable",
                        icon: "lock",
                        hidden: props.status === "suspended" || props.status === "disabled" ? true : false,
                        disabled: props.status === "suspended" || props.status === "disabled" ? true : false,
                        onClick: () => props.disableMembership(),
                      },
                      {
                        label: "Enable",
                        icon: "unlock",
                        hidden: props.status === "disabled" ? false : true,
                        disabled: props.status !== "disabled" ? true : false,
                        onClick: () => props.enableMembership(),
                      },
                      {
                        label: t("Suspend"),
                        icon: "ban",
                        hidden: props.status === "suspended" || props.status === "disabled" ? true : false,
                        disabled: props.status === "suspended" || props.status === "disabled" ? true : false,
                        onClick: () => props.suspendMembership(),
                      },
                      {
                        label: props.status === "active" ? "Remove Suspension" : "Resume Membership",
                        icon: "refresh",
                        hidden: props.status === "disabled" || (props.status === "active" && !props.suspension_start_date),
                        disabled: props.status === "disabled" || (props.status === "active" && !props.suspension_start_date),
                        onClick: () => props.unsuspendMembership(props.id),
                      },
                    ],
                    [
                      {
                        label: t("Delete"),
                        icon: "trash",
                        onClick: () => props.deleteMembership(props.id),
                      },
                    ],
                  ]}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MembershipDisplay;
